import { useState } from "react";
import { getApiUrl } from "@/config/api";
import { authenticatedFetch } from "@/utils/apiUtils";
import { RiskAlertReview, CoachAction } from "@/types";

interface Props {
  review: RiskAlertReview;
  onClose: () => void;
  onSaved: () => void;
}

const OPTIONS: { value: CoachAction; label: string }[] = [
  { value: "no_action", label: "No action taken" },
  { value: "load_adjustment", label: "Load adjustment" },
  { value: "medical_check", label: "Medical check / consult" },
  { value: "removed_from_practice", label: "Removed from practice" },
  { value: "other", label: "Other (see notes)" },
];

export default function AlertActionModal({ review, onClose, onSaved }: Props) {
  const initialAction = review.coach_action === "pending" ? "no_action" : review.coach_action;
  const [action, setAction] = useState<CoachAction>(initialAction);
  const [notes, setNotes] = useState<string>(review.coach_notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const result = await authenticatedFetch<RiskAlertReview>(
      getApiUrl(`/api/alert-reviews/${review.id}/action/`),
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ coach_action: action, coach_notes: notes }),
      },
      "recording alert action",
    );
    setSaving(false);
    if (result.success) {
      onSaved();
    } else {
      setError("Could not save. Try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="cgm-card p-5 w-full max-w-md space-y-4"
      >
        <h3 className="text-sm font-semibold cgm-text-primary">
          Record action for {review.athlete_name}
        </h3>
        <p className="text-xs cgm-text-secondary">
          {review.alert_date} · {review.tier} · risk {review.risk_score.toFixed(2)}
          {review.top_signal && <> · top signal: <span className="font-mono">{review.top_signal}</span></>}
        </p>

        <div className="space-y-2">
          <label className="text-xs cgm-text-secondary block">Action</label>
          <select
            className="w-full cgm-input text-sm"
            value={action}
            onChange={(e) => setAction(e.target.value as CoachAction)}
          >
            {OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-xs cgm-text-secondary block">Notes (optional)</label>
          <textarea
            className="w-full cgm-input text-sm"
            rows={3}
            maxLength={2000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="text-xs cgm-text-secondary underline"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="text-xs cgm-text-primary underline disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
